import { PROJECT_KEYS } from '@/Shared/constants/keys/keys';
import { callOverlay } from '@/Groups/OverlayManager/public/utils/callOverlay';
import store from '@/Shared/store/index';

const abortExemptEndpoints = [
  '/api/alpha/inner-pages',
  '/api/auth/users/me/',
  '/api/widgets/user-assets/'
];

// Хранилище контроллеров для каждого URL только для fetchWithAuth
const abortControllers = new Map();

export const fetchWithAuth = async (url, options = {}) => {
  const endpointKey = extractEndpointKey(url);

  const isAbortExempt = abortExemptEndpoints.some(exempt => endpointKey.startsWith(exempt));

  if (!isAbortExempt && abortControllers.has(endpointKey)) {
    abortControllers.get(endpointKey).abort();
  }

  const controller = new AbortController();
  abortControllers.set(endpointKey, controller);
  options.signal = controller.signal;

  const accessToken = store.getters.getAccessToken;
  if (accessToken) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
    };
  }

  try {
    let response = await fetch(url, options);

    if (response.status === 401) {
      await refreshAccessToken();
      options.headers['Authorization'] = `Bearer ${store.getters.getAccessToken}`;
      response = await fetch(url, options);
    }

    abortControllers.delete(endpointKey);
    return response;
  } catch (error) {
    abortControllers.delete(endpointKey);
    if (error.name === 'AbortError') return;
    callOverlay({ text: 'Сервер временно недоступен. Пожалуйста, попробуйте позже', color: 'red', icon: 'wifi' });
    console.error('Сервер недоступен:', error);
    throw new Error(`Сервер недоступен: ${error}`);
  }
};

// Токен авторизации
const AUTH_TOKEN = PROJECT_KEYS.AUTH_TOKEN;

export const fetchAIAgentDataWithAuth = async (url, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(AUTH_TOKEN && { 'Authorization': `Token ${AUTH_TOKEN}` }),
  };

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error('Error in fetchWithAuth:', error);
    throw error;
  }
};

const refreshAccessToken = async () => {
  try {
    const response = await fetch(`${store.getters.getBackendUrl}/api/token/refresh/`, {
      method: 'POST',
      credentials: 'include', // Включаем отправку куков
      headers: {'Content-Type': 'application/json'},
    });

    if (!response.ok) {
      if (response.status === 401) {
        const errorData = await response.json();
        if (errorData.detail) {
          console.log(`${errorData.detail} (${errorData.code})`);
        }
      }
      throw new Error(`Failed to refresh access token: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    await store.dispatch('saveAccessToken', data.access);
  } catch (error) {
    console.error('Error refreshing access token:', error);
  }
};

export function getCookie(name) {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop().split(';').shift();
      return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
  } catch (error) {
    console.error(`Error getting cookie ${name}:`, error);
    return null;
  }
}

export function setCookie(name, value, days, options = {}) {
  try {
    const {
      path = '/',
      domain = '',
      secure = true,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${name}=${encodeURIComponent(value || '')}`;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  } catch (error) {
    console.error(`Error setting cookie ${name}:`, error);
  }
}

export function deleteCookie(name, options = {}) {
  try {
    const {
      path = '/',
      domain = '',
      secure = true,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  } catch (error) {
    console.error(`Error deleting cookie ${name}:`, error);
  }
}

const extractEndpointKey = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname; // только путь, без query
  } catch (e) {
    console.warn('Invalid URL in fetchWithAuth:', url);
    return url; // fallback — пусть будет как есть
  }
}